import { CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  generatedContentApi,
  GeneratedScript,
  GenerateScriptParams,
} from 'src/api/generatedContent';
import styles from './ScriptContentForm.module.scss';

interface ScriptContentFormProps {
  onContentGenerated: (script: GeneratedScript) => void;
  initialData?: GenerateScriptParams | null;
}

const ScriptContentForm: React.FC<ScriptContentFormProps> = ({
  onContentGenerated,
  initialData,
}) => {
  const [formData, setFormData] = useState<GenerateScriptParams>({
    topic: '',
    coachingGoal: '',
    keyPoints: [''],
  });

  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        topic: initialData.topic || '',
        coachingGoal: initialData.coachingGoal || '',
        keyPoints:
          initialData.keyPoints && initialData.keyPoints.length > 0
            ? [...initialData.keyPoints]
            : [''],
      });
    }
  }, [initialData]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleKeyPointChange = (index: number, value: string) => {
    const updatedKeyPoints = [...formData.keyPoints];
    updatedKeyPoints[index] = value;
    setFormData((prev) => ({
      ...prev,
      keyPoints: updatedKeyPoints,
    }));
  };

  const addKeyPoint = () => {
    setFormData((prev) => ({
      ...prev,
      keyPoints: [...prev.keyPoints, ''],
    }));
  };

  const removeKeyPoint = (index: number) => {
    if (formData.keyPoints.length <= 1) {
      return;
    }
    const updatedKeyPoints = formData.keyPoints.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      keyPoints: updatedKeyPoints,
    }));
  };

  const handleGenerateContent = async () => {
    if (!formData.coachingGoal || !formData.topic || !formData.keyPoints) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      setIsGenerating(true);

      const filteredKeyPoints = formData.keyPoints.filter(
        (point) => point.trim() !== ''
      );

      const params: GenerateScriptParams = {
        ...formData,
        keyPoints: filteredKeyPoints.length > 0 ? filteredKeyPoints : [''],
        planId: initialData?.planId,
        challengeId: initialData?.challengeId,
      };

      const generatedScript = await generatedContentApi.generateScript(params);

      onContentGenerated(generatedScript);

      toast.success('Script generated successfully!');
    } catch (error) {
      toast.error('Failed to generate script content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <h2>Create Script</h2>

      <div className={styles.formGroup}>
        <TextField
          label="Topic"
          name="topic"
          value={formData.topic}
          onChange={handleInputChange}
          fullWidth
          required
          margin="normal"
        />
      </div>

      <div className={styles.formGroup}>
        <TextField
          label="Script Goal"
          name="coachingGoal"
          value={formData.coachingGoal}
          onChange={handleInputChange}
          fullWidth
          required
          margin="normal"
        />
      </div>

      <div className={styles.formGroup}>
        {formData.keyPoints.map((point, index) => (
          <div key={index} className={styles.keyPointInput}>
            <TextField
              variant="outlined"
              fullWidth
              value={point}
              onChange={(e) => handleKeyPointChange(index, e.target.value)}
              placeholder={`Key point ${index + 1}`}
            />
            <button
              type="button"
              onClick={() => removeKeyPoint(index)}
              className={styles.removeButton}
              disabled={formData.keyPoints.length <= 1}
            >
              X
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addKeyPoint}
          className={styles.addButton}
        >
          + Add Key Point
        </button>
      </div>

      <div className={styles.buttonContainer}>
        <button
          onClick={handleGenerateContent}
          disabled={
            isGenerating ||
            !formData.topic ||
            !formData.coachingGoal ||
            !formData.keyPoints
          }
          className={styles.generateButton}
        >
          {isGenerating ? (
            <>
              <CircularProgress size={24} color="inherit" />
              <span className={styles.loadingText}>Generating...</span>
            </>
          ) : (
            'Generate Video Script'
          )}
        </button>
      </div>
    </div>
  );
};

export default ScriptContentForm;
